import { useRef } from "react";
import { safeToLowerCase } from "../../../../shared-functions/string";
import {
  getFullRegionName,
  renderZonesForSelectBox,
} from "../../../../app_shared_functions";
import { useSelector } from "react-redux";
import { getDomains, getProjectsObject } from "../../../../selectors/projects";
import {
  Checkbox,
  Grid,
  Icon,
  Input,
  Popup,
  Ref,
  Select,
} from "semantic-ui-react";
import FancyHeader from "../../../../components/shared/FancyHeader";
import { formFieldWarningClassName } from "../../../../shared-functions/form";
import useSubnetCreateForm from "./hooks/useSubnetCreateForm";
import { PredefinedParams } from "../../../../components/slidingpanel/type";
import IPVersion from "./bits/IPVersion";
import IPv4Settings from "./bits/IPv4Settings";

import useResetOnRegionChange from "./hooks/useResetOnRegionChange";
import IPv6Settings from "./bits/ipv6/IPv6Settings";
import Network from "./bits/network/Network";
import useCreateClickErrorHandler from "./hooks/useCreateClickErrorHandler";
import useSubnetCreateAction from "./hooks/useSubnetCreateAction";

type SubnetCreateProps = {
  predefined_params?: PredefinedParams;
  createAnother: boolean;
  changeCreateAnother: () => void;
  closeSlidingMenuLayer: () => void;
};

const SubnetCreate = ({
  predefined_params,
  createAnother,
  changeCreateAnother,
  closeSlidingMenuLayer,
}: SubnetCreateProps) => {
  const nameRef = useRef<HTMLElement | null>(null);
  const regionRef = useRef<HTMLElement | null>(null);
  const networkRef = useRef<HTMLElement | null>(null);

  const userDomains = useSelector(getDomains);
  const projects = useSelector(getProjectsObject);

  const areasList = renderZonesForSelectBox(projects, userDomains);

  const {
    error,
    formData,
    handleInputChange,
    handleSelectChange,
    formWarning,
    showFormWarning,
  } = useSubnetCreateForm({
    nameRef,
    regionRef,
    networkRef,
    predefined_params,
  });

  useResetOnRegionChange({
    region: formData.region || predefined_params?.region,
    handleInputChange,
  });

  const { handleSubnetCreation, isCreating } = useSubnetCreateAction({
    formData,
    predefined_params,
    createAnother,
  });

  const onDisabledCreateClick = useCreateClickErrorHandler({
    error,
    showFormWarning,
  });

  return (
    <div className={`creator-component-wrapper`}>
      <div>
        <FancyHeader title="Create a Subnet" knowledgeBase />

        <p></p>

        <Grid>
          <Grid.Row className="separator padding-top-30">
            {/* NAME */}
            <Grid.Column textAlign="left" width={8} className="flex vcenter">
              <h5>Name</h5>
            </Grid.Column>
            <Grid.Column textAlign="left" width={8}>
              <Ref innerRef={nameRef}>
                <Input
                  value={formData.name}
                  name="name"
                  className={`select-box full ${formFieldWarningClassName(
                    formWarning,
                    error?.ref,
                    nameRef,
                  )}`}
                  onChange={handleInputChange}
                />
              </Ref>
            </Grid.Column>

            {/* REGION */}
            <Grid.Column
              textAlign="left"
              width={8}
              className="flex vcenter margin-top-30"
            >
              <h5>Region</h5>
            </Grid.Column>
            <Grid.Column
              textAlign="left"
              width={8}
              className="flex vcenter margin-top-30"
            >
              <Ref innerRef={regionRef}>
                <Select
                  disabled={Boolean(predefined_params?.region)}
                  icon="chevron circle down"
                  className={`select-box full ${formFieldWarningClassName(
                    formWarning,
                    error?.ref,
                    regionRef,
                  )}`}
                  placeholder={
                    predefined_params?.region
                      ? getFullRegionName(
                          userDomains,
                          safeToLowerCase(predefined_params.region),
                        )
                      : "Choose Region"
                  }
                  options={areasList}
                  name="region"
                  onChange={handleSelectChange}
                />
              </Ref>
            </Grid.Column>
          </Grid.Row>

          {(formData.region || predefined_params?.region) && (
            <>
              <Grid.Row className="separator padding-top-30">
                <IPVersion
                  onChange={handleSelectChange}
                  value={formData.ipVersion}
                />

                {formData.ipVersion === "4" && (
                  <IPv4Settings
                    onChange={handleInputChange}
                    value={formData.cidr}
                  />
                )}

                {formData.ipVersion === "6" && (
                  <IPv6Settings
                    region={formData.region || predefined_params?.region}
                    onChange={handleSelectChange}
                    selectedSubnetPoolId={formData.selectedSubnetPoolId}
                    addressingType={formData.addressingType}
                  />
                )}
              </Grid.Row>

              <Network
                region={formData.region || predefined_params?.region}
                selectedNetwork={formData.selectedNetwork}
                onChange={handleSelectChange}
                errorClassName={formFieldWarningClassName(
                  formWarning,
                  error?.ref,
                  networkRef,
                )}
              />
            </>
          )}

          <Grid.Row className="padding-top-00">
            <Grid.Column textAlign="left" width={16}>
              {error ? (
                <Popup
                  trigger={
                    <button
                      className="float-right button button--green button--disabled button--icon__left"
                      onClick={onDisabledCreateClick}
                    >
                      <Icon name="exclamation circle" />
                      <span>Create</span>
                    </button>
                  }
                >
                  {error?.message}
                </Popup>
              ) : isCreating ? (
                <button className="float-right button button--green overflow-hidden button--icon__right">
                  <Icon loading name="spinner" />
                  <span>Creating</span>
                </button>
              ) : (
                <button
                  className="float-right button button--green"
                  onClick={handleSubnetCreation}
                >
                  <span>Create</span>
                </button>
              )}

              <Checkbox
                className="simple-checkbox float-right margin-top-half  "
                label="Create Another "
                checked={createAnother}
                onChange={changeCreateAnother}
              />
              <button
                className="button button--bordered"
                onClick={closeSlidingMenuLayer}
              >
                <span>Back</span>
              </button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </div>
  );
};

export default SubnetCreate;
